import { doc, setDoc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { SubmissionWorkflow } from '../types/submission-workflow';

export class WorkflowService {
  private static readonly COLLECTION = 'submission_workflows';

  /**
   * Save workflow to Firestore
   */
  static async saveWorkflow(workflow: SubmissionWorkflow): Promise<void> {
    const workflowRef = doc(db, this.COLLECTION, workflow.id);
    
    // Ensure dates are valid before saving
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24 hours from now
    
    const workflowData = {
      ...workflow,
      // Use serverTimestamp() for Firestore-managed timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Ensure expiresAt is a valid Date object
      expiresAt: expiresAt
    };

    await setDoc(workflowRef, workflowData);
  }

  /**
   * Update workflow in Firestore
   */
  static async updateWorkflow(workflowId: string, updates: Partial<SubmissionWorkflow>): Promise<void> {
    const workflowRef = doc(db, this.COLLECTION, workflowId);
    
    // Clean up any invalid dates in updates
    const cleanUpdates = { ...updates };
    
    // Ensure any Date fields are valid
    if (cleanUpdates.expiresAt && !(cleanUpdates.expiresAt instanceof Date)) {
      delete cleanUpdates.expiresAt;
    }
    
    // Clean up metadata dates if present
    if (cleanUpdates.data?.metadata?.pickupDate) {
      const pickupDate = cleanUpdates.data.metadata.pickupDate;
      if (!(pickupDate instanceof Date) || isNaN(pickupDate.getTime())) {
        // Remove invalid pickup date
        cleanUpdates.data = {
          ...cleanUpdates.data,
          metadata: {
            ...cleanUpdates.data.metadata,
            pickupDate: undefined
          }
        };
      }
    }
    
    const updateData = {
      ...cleanUpdates,
      updatedAt: serverTimestamp()
    };

    await updateDoc(workflowRef, updateData);
  }

  /**
   * Get workflow from Firestore
   */
  static async getWorkflow(workflowId: string): Promise<SubmissionWorkflow | null> {
    const workflowRef = doc(db, this.COLLECTION, workflowId);
    const workflowDoc = await getDoc(workflowRef);
    
    if (!workflowDoc.exists()) {
      return null;
    }

    const data = workflowDoc.data();
    
    // Convert Firestore timestamps back to Date objects
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      expiresAt: data.expiresAt?.toDate() || new Date()
    } as SubmissionWorkflow;
  }

  /**
   * Mark workflow as completed
   */
  static async completeWorkflow(workflowId: string, reportId: string): Promise<void> {
    await this.updateWorkflow(workflowId, {
      status: 'completed',
      reportId
    });
  }

  /**
   * Mark workflow as cancelled
   */
  static async cancelWorkflow(workflowId: string): Promise<void> {
    await this.updateWorkflow(workflowId, {
      status: 'cancelled'
    });
  }
}